import React from 'react';
import { Link } from 'react-router-dom';
import { cx } from './cx';
import { focusRing } from './focusRing';

export interface ListProps {
  /** Rows sit on the inset surface inside a card instead of on their own card. */
  inset?: boolean;
  className?: string;
  children: React.ReactNode;
}

/** Container for ListRow: one card (the style's card treatment, k-card) or inset block, with dividers between rows. */
export function List({ inset = false, className, children }: ListProps) {
  return (
    <div
      role="list"
      className={cx(
        'flex flex-col overflow-hidden border',
        inset ? 'rounded-control border-edge-subtle bg-inset' : 'rounded-container k-card bg-card',
        '[&>*+*]:border-t [&>*+*]:border-edge-subtle',
        className
      )}
    >
      {children}
    </div>
  );
}

export interface ListRowProps {
  leading?: React.ReactNode;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  meta?: React.ReactNode;
  trailing?: React.ReactNode;
  onClick?: () => void;
  href?: string;
  unread?: boolean;
  /** Screen-reader text for the unread dot (translated). */
  unreadLabel?: string;
  disabled?: boolean;
  /** Title, subtitle and meta wrap onto more lines instead of being cut off (sentence hints on phones). */
  wrap?: boolean;
  className?: string;
}

/** One row: optional leading media, title + subtitle + meta, trailing value or badge. */
export function ListRow({ leading, title, subtitle, meta, trailing, onClick, href, unread, unreadLabel, disabled, wrap = false, className }: ListRowProps) {
  const fit = wrap ? 'min-w-0 break-words' : 'truncate';
  const body = (
    <>
      {leading ? <span className="inline-flex shrink-0 items-center">{leading}</span> : null}
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="flex min-w-0 items-center gap-2 font-ui text-body font-semibold text-primary">
          {unread ? (
            <>
              <span aria-hidden className="size-2 shrink-0 rounded-full bg-action-primary" />
              {unreadLabel ? <span className="sr-only">{unreadLabel}</span> : null}
            </>
          ) : null}
          <span className={fit}>{title}</span>
        </span>
        {subtitle ? <span className={cx(fit, 'font-ui text-small text-secondary')}>{subtitle}</span> : null}
        {meta ? <span className={cx(fit, 'font-ui text-small text-tertiary')}>{meta}</span> : null}
      </span>
      {trailing ? <span className="flex shrink-0 flex-col items-end gap-1 text-small">{trailing}</span> : null}
    </>
  );
  const base = cx(
    'grid w-full min-w-0 items-center gap-3 px-3.5 py-3 text-left',
    leading ? 'grid-cols-[auto_minmax(0,1fr)_auto]' : 'grid-cols-[minmax(0,1fr)_auto]',
    className
  );
  const interactive = cx(base, 'cursor-pointer transition-colors duration-(--k-dur-fast) hover:bg-hover', focusRing, disabled && 'pointer-events-none text-disabled');
  if (href && !disabled) {
    return (
      <div role="listitem" className="contents">
        <Link to={href} className={interactive}>
          {body}
        </Link>
      </div>
    );
  }
  if (onClick) {
    return (
      <div role="listitem" className="contents">
        <button type="button" onClick={onClick} disabled={disabled} className={cx(interactive, 'border-0 bg-transparent')}>
          {body}
        </button>
      </div>
    );
  }
  return (
    <div role="listitem" className={base}>
      {body}
    </div>
  );
}

export default List;
